import cx from 'classnames';
import React, { useEffect, useRef } from 'react';

type OwnProps = React.PropsWithChildren<{
  className?: string;
  isCollapsed: boolean;
}>;

/**
 * A Collapse component that handles smooth height transitions.
 */
export default function Collapse({
  isCollapsed,
  children,
  className = ''
}: OwnProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const prevTimeRef = useRef<number>(0);
  const isInitialRender = useRef<boolean>(true);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;

      return;
    }

    if (!wrapperRef.current || !contentRef.current) return;

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const contentHeight = contentRef.current.offsetHeight;

    wrapperRef.current.style.height = isCollapsed
      ? `${contentHeight}px`
      : '0px';

    prevTimeRef.current = 0;

    const animate = (timestamp: number) => {
      if (!wrapperRef.current || !contentRef.current) return;

      const change = prevTimeRef.current ? timestamp - prevTimeRef.current : 10;
      prevTimeRef.current = timestamp;

      const currentHeight = wrapperRef.current.offsetHeight;
      const newHeight = currentHeight + (isCollapsed ? -change : change);

      if (isCollapsed && newHeight <= 0) {
        wrapperRef.current.style.height = '0px';
      } else if (!isCollapsed && newHeight >= contentHeight) {
        wrapperRef.current.style.height = 'auto';
      } else {
        wrapperRef.current.style.height = `${newHeight}px`;
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isCollapsed]);

  return (
    <div
      ref={wrapperRef}
      className={cx('overflow-hidden', className)}
      style={{
        height: isCollapsed ? '0px' : 'auto'
      }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
}
